// Finance Forecaster landing page (/).
// Story (David, 2026-11-03): we support couple communication to reduce the
// friction and stress money creates in relationships, through a calm venue,
// tools for healthy money dialogue, and a rhythm that guides early and regular
// conversations. The forecast is the tool, not the point.
// Copy: docs/brand/landing-copy-v3.md. Brand: docs/brand/Finance-Forecaster-Brand-Book.pdf.

import React from 'react';
import { Link } from 'react-router-dom';
import SignupLink from '../Auth/SignupLink';
import SEOHelmet from '../SEO/SEOHelmet';
import EmailCaptureForm from '../EmailCapture/EmailCaptureForm';
import MarketingNav from './MarketingNav';
import MarketingFooter from './MarketingFooter';
import {
  BTN_ON_BLUE, BTN_GHOST_ON_BLUE, Section, SectionHead, Card, Note, MockButton,
  ForecastPanel, MiniForecastLine, AskExchange,
} from './marketingKit';

const Check = () => (
  <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

// ============================================================================
// HERO
// ============================================================================
const HeroSection = () => (
  <section className="relative overflow-hidden bg-finance-blue text-white">
    <MarketingNav ground="blue" />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 lg:pt-20 lg:pb-28">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
        <div className="text-center lg:text-left">
          <h1 className="text-display-sm sm:text-display">
            See where your money is going.
            <span className="block text-forecast-green">Together.</span>
          </h1>
          <p className="mt-6 text-body sm:text-xl text-white/90 max-w-xl mx-auto lg:mx-0">
            Money can be one of the hardest things for couples to talk about. Finance Forecaster
            was built to help couples get on the same team about their money.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <SignupLink className={BTN_ON_BLUE} closedLabel="Join the waitlist">
              Get started
            </SignupLink>
            <Link to="/what-is" className={BTN_GHOST_ON_BLUE}>
              See how it works
            </Link>
          </div>

          <ul className="mt-10 space-y-3 text-sm text-white/90 max-w-md mx-auto lg:mx-0 text-left">
            <li className="flex items-start gap-3"><Check /><span>Read-only bank connections through Plaid. We never see your login.</span></li>
            <li className="flex items-start gap-3"><Check /><span>Share everything, or keep an account private. Household totals use only what&rsquo;s shared.</span></li>
          </ul>
        </div>

        {/* Between the two of you: the forecast, one heads-up, one decision */}
        <div className="mt-16 lg:mt-0">
          <div className="bg-white dark:bg-midnight-800 rounded-2xl shadow-card p-5 sm:p-6 max-w-md mx-auto text-midnight dark:text-white space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-bold text-finance-blue">Between the two of you</p>
              <span className="text-xs font-medium text-midnight/75 dark:text-white/75 bg-mist dark:bg-midnight-700 rounded-full px-3 py-1">Dana and Sam</span>
            </div>
            <ForecastPanel compact />
            <Note on="white" label="Worth a word">
              Eating out is heading about $120 over usual. Something to consider with date night in a few weeks.
            </Note>
            <Note on="white" label="Suggested by Sam.">
              <p className="font-bold text-[15px]">Landscaping this spring, about $4,800?</p>
              <p className="mt-1 text-midnight/75 dark:text-white/75">
                Sam proposed April. All of it in April causes a near term shortfall; you will likely make up the difference in May.
              </p>
              <div className="mt-3 flex gap-2" aria-hidden="true">
                <MockButton primary>Agree</MockButton>
                <MockButton>Adjust</MockButton>
              </div>
            </Note>
            <p className="text-xs text-midnight/70 dark:text-white/70">Next time you talk.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============================================================================
// WHAT IT IS
// ============================================================================
const WhatItIsSection = () => (
  <Section ground="white" className="pt-20 pb-8" labelledBy="what-it-is">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16">
      <SectionHead eyebrow="What it is" title="Money shouldn’t be such a hard conversation." id="what-it-is" className="mb-6 lg:mb-0" />
      <div className="text-body text-midnight/75 dark:text-white/75 space-y-4">
        <p>
          Money can be an emotionally charged topic for couples. It can feel like you are on different
          teams. Our goal for Finance Forecaster is to help couples have the same vision for their money
          and help them consistently stay on the same page.
        </p>
        <p>
          Finance Forecaster is built to support your relationship. We provide a place where you can set a
          vision that you both want and provide a gentle rhythm that starts the conversation early and keeps
          it going, so it never has to become a confrontation. We handle the backend numbers, tracking,
          categorization, etc. so you can stay focused on where you and your family are headed.
        </p>
      </div>
    </div>
  </Section>
);

// ============================================================================
// THE VENUE
// ============================================================================
const VenueSection = () => (
  <Section ground="white" className="py-16" labelledBy="venue">
    <SectionHead eyebrow="The venue" title="A calm place to talk about money" id="venue" className="mb-10" />
    <div className="grid md:grid-cols-3 gap-6">
      <Card className="flex flex-col gap-4">
        <h3 className="text-heading-sm text-midnight dark:text-white">The same picture</h3>
        <p className="text-midnight/75 dark:text-white/75">
          One forecast of the month ahead that you both see. Nobody has to ask how things are, and nobody is
          the only one who knows.
        </p>
        <Note on="mist" className="mt-auto">
          <p className="text-midnight/75 dark:text-white/75">You&rsquo;re on track for November.</p>
          <MiniForecastLine />
          <p className="text-xl font-extrabold tabular-nums">$6,240 <span className="text-sm font-medium text-midnight/70 dark:text-white/70">forecast &middot; $120 to spare</span></p>
        </Note>
      </Card>

      <Card className="flex flex-col gap-4">
        <h3 className="text-heading-sm text-midnight dark:text-white">The numbers do the talking</h3>
        <p className="text-midnight/75 dark:text-white/75">
          When something is worth a word, it arrives as a category and an amount, never a person, and as a
          question, never a warning. It goes to whoever handles that part of the month so nobody gets ambushed.
        </p>
        <Note on="mist" className="mt-auto space-y-3">
          <p><strong>Groceries</strong> are tracking $140 under your usual spending. Good opportunity to bank a little extra this month.</p>
          <p className="border-t border-mist-200 dark:border-midnight-600 pt-3"><strong>Eating out</strong> is heading about $120 over usual. Something to consider with date night next week.</p>
        </Note>
      </Card>

      <Card className="flex flex-col gap-4">
        <h3 className="text-heading-sm text-midnight dark:text-white">Decisions made together, in turns</h3>
        <p className="text-midnight/75 dark:text-white/75">
          One of you puts it on the table and Finance Forecaster helps run the forecast to give you the
          information you need. Then, you two talk about it. The numbers can move around but what is critical
          is that you stay on the same page.
        </p>
        <Note on="mist" label="Decided together" className="mt-auto">
          <p className="font-bold text-[15px]">Landscaping this spring.</p>
          <p className="mt-1 text-midnight/75 dark:text-white/75">Dana proposed April. Sam adjusted: half in April, half in May. Included in the forecast moving forward.</p>
        </Note>
      </Card>
    </div>
  </Section>
);

// ============================================================================
// THE RHYTHM
// ============================================================================
const RHYTHM = [
  {
    eyebrow: 'At the start',
    title: 'Find out where you differ, before it costs anything',
    body: 'When you set up, you each answer a few questions on your own: who handles what, what matters most this year. Where your answers don’t match becomes your first conversation, while it’s still easy.',
    mock: <><strong>Private school:</strong> important to Dana but not so much for Sam.</>,
  },
  {
    eyebrow: 'Every week',
    title: 'A short note, and a few minutes if there’s something in it',
    body: 'Once a week, at a time you pick, you spend time getting on the same page. Usually, this is a short conversation but keeps you both on the same page. This provides a consistent avenue to work through bigger money decisions so that it isn’t such a sporadic, tense conversation.',
    mock: 'On track for November. Nothing running over.',
  },
  {
    eyebrow: 'Over time',
    title: 'Agreements that hold, without anyone policing them',
    body: 'What you decide together is kept, and we keep an eye on it so neither of you has to. When something drifts, it comes up early and small, as a question, not a verdict.',
    mock: <><strong>Agreed in October:</strong> Spring Break trip to Florida.</>,
  },
];

const RhythmSection = () => (
  <Section ground="mist" labelledBy="rhythm">
    <SectionHead ground="mist" eyebrow="The rhythm" title="Conversations that start early and keep going" id="rhythm" className="mb-10">
      <p>
        Most couples talk about money when it&rsquo;s already tight. We help you start before that, and keep it
        light enough that it keeps happening. Not a budget meeting. A few minutes, and most weeks the whole
        message is &ldquo;we&rsquo;re fine.&rdquo;
      </p>
    </SectionHead>
    <ol className="grid md:grid-cols-3 gap-6">
      {RHYTHM.map((r) => (
        <li key={r.eyebrow}>
          <Card on="mist" className="h-full flex flex-col gap-3">
            <p className="text-xs font-bold text-finance-blue">{r.eyebrow}</p>
            <h3 className="text-heading-sm text-midnight dark:text-white">{r.title}</h3>
            <p className="text-midnight/75 dark:text-white/75">{r.body}</p>
            <Note on="white" className="mt-auto">{r.mock}</Note>
          </Card>
        </li>
      ))}
    </ol>
  </Section>
);

// ============================================================================
// WHAT CHANGES
// ============================================================================
const CHANGES = [
  { title: 'No one carries it alone', body: 'Both of you see the same picture, so the one who runs the month isn’t also the one who has to explain it.' },
  { title: 'Fewer fights about small things', body: 'The $40 dinner stops being a proxy for everything else, because everything else is already in view.' },
  { title: 'Big decisions get decided', body: 'Private school, the house, a part-time year: conversations that keep you on the same team and the same page.' },
  { title: 'Money becomes something you’re good at together', body: 'Not a source of stress between you. One more thing the two of you handle well.' },
];

const ChangesSection = () => (
  <Section ground="midnight" labelledBy="changes">
    <SectionHead ground="midnight" eyebrow="What changes" title="Less friction. Fewer surprises. The same page." id="changes" className="mb-10" />
    <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {CHANGES.map((c) => (
        <li key={c.title} className="rounded-2xl bg-midnight-700 p-6">
          <h3 className="text-lg font-extrabold">{c.title}</h3>
          <p className="mt-2 text-white/80">{c.body}</p>
        </li>
      ))}
    </ul>
  </Section>
);

// ============================================================================
// HOW IT WORKS
// ============================================================================
const STEPS = [
  { title: 'Connect your accounts', body: 'Through Plaid, read-only. Mark any account private and it stays out of your partner’s view and the household totals.' },
  { title: 'We learn how your month runs', body: 'Paychecks on different schedules, the mortgage, childcare, the Target runs. Nobody categorizes anything, and the forecast is forward-looking, not a report on last month.' },
  { title: 'Invite your partner', body: 'They see the same picture the moment they join. You each answer a few questions on your own, and your first conversation is ready.' },
  { title: 'Keep talking, a little at a time', body: 'A short note each week, decisions when they come up, agreements that unify you two. Money stops being the thing you avoid.' },
];

const HowItWorksSection = () => (
  <Section ground="white" id="how" labelledBy="how-it-works">
    <SectionHead eyebrow="How it works" title="Set it up once. The forecast updates every day." id="how-it-works" className="mb-10" />
    <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {STEPS.map((s, i) => (
        <li key={s.title} className="rounded-2xl border border-mist-200 dark:border-midnight-600 p-6">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-finance-blue text-white font-extrabold tabular-nums">{i + 1}</span>
          <h3 className="mt-4 text-lg font-extrabold text-midnight dark:text-white">{s.title}</h3>
          <p className="mt-2 text-midnight/75 dark:text-white/75">{s.body}</p>
        </li>
      ))}
    </ol>
  </Section>
);

// ============================================================================
// ASK
// ============================================================================
const AskSection = () => (
  <Section ground="white" className="pb-20" labelledBy="ask">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <SectionHead eyebrow="Ask" title="Ask it the way you’d ask each other" id="ask" className="mb-8 lg:mb-0">
        <p>
          Plain words in, an answer that uses your real numbers out. Sometimes it&rsquo;s easier to ask the app
          first to help set a baseline for your conversation.
        </p>
      </SectionHead>
      <AskExchange
        question="How would a 50% increase in our mortgage impact our ability to save for college? Retire early?"
        answer="Increasing your mortgage by 50% would tighten your monthly cash flow quite a bit. However, we can find areas to make it manageable if it is important to you."
      />
    </div>
  </Section>
);

// ============================================================================
// WHO IT'S FOR
// ============================================================================
const QUESTIONS = [
  'Should one of us go part-time?',
  'Can we afford private school?',
  'Can we carry a bigger house?',
  'We earn well. Where does it go?',
];

const WhoSection = () => (
  <Section ground="mist" labelledBy="who">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
      <SectionHead ground="mist" eyebrow="Who it’s for" title="You two earn well. Why does it still feel tight?" id="who" className="mb-8 lg:mb-0">
        <p>
          Paychecks, kids in activities, a mortgage you like, and a future you&rsquo;re building on purpose.
          Usually one of you runs the month and the other isn&rsquo;t in the loop the way they&rsquo;d like to be.
          Finance Forecaster is built for you.
        </p>
        <p>
          <Link to="/who-its-for" className="font-bold text-finance-blue underline underline-offset-4">See who it&rsquo;s for</Link>
        </p>
      </SectionHead>
      <ul className="grid sm:grid-cols-2 gap-4">
        {QUESTIONS.map((q) => (
          <li key={q} className="rounded-xl bg-white dark:bg-midnight-700 p-5 font-bold text-midnight dark:text-white">{q}</li>
        ))}
      </ul>
    </div>
  </Section>
);

// ============================================================================
// CALCULATORS
// ============================================================================
const CALCULATORS = [
  { slug: 'private-school-calculator', title: 'Private school', body: 'Tuition for every child, every year through graduation.' },
  { slug: 'one-income-calculator', title: 'One income', body: 'The real monthly cost after taxes, childcare and work expenses.' },
  { slug: 'move-up-calculator', title: 'Bigger house', body: 'The lender’s maximum and the price your cash flow can carry.' },
];

const CalculatorsSection = () => (
  <Section ground="white" labelledBy="calculators">
    <SectionHead eyebrow="Free calculators" title="Run the numbers before you decide" id="calculators" className="mb-10">
      <p>
        Three free calculators for the decisions that shape a household&rsquo;s cash flow for years. Every year,
        not just the first, with one honest verdict: comfortable, tight, or comes up short. Send a copy to your
        partner so you&rsquo;re both looking at the same answer.
      </p>
    </SectionHead>
    <div className="grid md:grid-cols-3 gap-5">
      {CALCULATORS.map((c) => (
        <a key={c.slug} href={`/${c.slug}`} className="block rounded-2xl border border-mist-200 dark:border-midnight-600 p-6 hover:border-finance-blue transition-colors">
          <span className="block text-lg font-extrabold text-midnight dark:text-white">{c.title}</span>
          <span className="mt-1 block text-midnight/75 dark:text-white/75">{c.body}</span>
        </a>
      ))}
    </div>
  </Section>
);

// ============================================================================
// WAITLIST
// ============================================================================
const WaitlistSection = () => (
  <Section ground="blue" id="waitlist" labelledBy="waitlist-title">
    <div className="max-w-2xl mx-auto text-center">
      <h2 id="waitlist-title" className="text-heading sm:text-display-sm">Ready to make money the easy conversation?</h2>
      <p className="mt-4 text-body text-white/90">
        Set it up once, invite your partner, and the next time money comes up you&rsquo;ll have the same picture,
        a calm place to talk, and a real answer. Not a guess, not a spreadsheet, not a fight.
      </p>
      <div className="mt-8 rounded-2xl bg-white dark:bg-midnight-800 p-5 text-left text-midnight dark:text-white">
        <EmailCaptureForm source="landing" />
      </div>
      <p className="mt-4 text-sm text-white/80">We&rsquo;ll email you when your spot opens. Nothing else.</p>
    </div>
  </Section>
);

// ============================================================================
// PAGE
// ============================================================================
const LandingPage = () => (
  <div className="min-h-screen font-brand bg-white dark:bg-midnight">
    <SEOHelmet
      title="See where your money is going. Together."
      description="Money can be one of the hardest things for couples to talk about. Finance Forecaster helps couples get on the same team about their money: one shared forecast, a calm place to talk, and decisions made together."
      canonicalPath="/"
    />
    <HeroSection />
    <WhatItIsSection />
    <VenueSection />
    <RhythmSection />
    <ChangesSection />
    <HowItWorksSection />
    <AskSection />
    <WhoSection />
    <CalculatorsSection />
    <WaitlistSection />
    <MarketingFooter />
  </div>
);

export default LandingPage;
